<template>
  <div class="tab-bar-container">
    <van-tabbar :model-value="active" class="tab-bar" :before-change="beforeChange" :safe-area-inset-bottom="true"
      :border="false" :route="true">
      <van-tabbar-item to="/" replace :icon="getAssetsFile('tabBar/home.png')">
        {{ t('tabbar.home') }}
      </van-tabbar-item>
      <van-tabbar-item to="/activity" replace :icon="getAssetsFile('tabBar/activity.png')">
        <div class="tab-content">
          {{ t('tabbar.activity') }}
          <!-- 活动红点 -->
          <RedDot v-if="hasActivityBadge" class="absolute -top-40px right-0px" />
          <!-- <div v-if="hasActivityBadge"
            class="absolute -top-40px right-0px w-[13px] h-[13px] transform -translate-y-1/4 translate-x-1/4">
            <div class="relative w-full h-full text-white text-white text-[8px] flex items-center justify-center">
              <img :src="getAssetsFile('home/messageUnreadIcon.png')" alt=""
                class=" absolute left-0 top-1px w-full h-[calc(100%+2px)]" />
            </div>
          </div> -->
        </div>
      </van-tabbar-item>
      <van-tabbar-item to="/Deposito" replace :icon="getAssetsFile('tabBar/share.png')">
        {{ t('tabbar.deposit') }}
      </van-tabbar-item>
      <van-tabbar-item to="/wallet" replace :icon="getAssetsFile('tabBar/wallet.png')">
        {{ t('tabbar.withdraw') }}
      </van-tabbar-item>
      <van-tabbar-item to="/person" replace :icon="getAssetsFile('tabBar/person.png')">
        <div class="tab-content">
          {{ t('tabbar.profile') }}

          <!-- 个人中心红点 -->
          <RedDot v-if="hasPersonBadge" class="absolute -top-40px right-0px" />
        </div>
      </van-tabbar-item>
    </van-tabbar>
  </div>
</template>
<script setup lang="ts">
import { RedDot } from "@/components/RedDot";
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useUserStore } from '@/stores'
import useUIStore from '@/stores/modules/ui'
import { useI18n } from 'vue-i18n'
import { useMessageStore } from '@/stores/modules/message'
import { useVipStore } from '@/stores/modules/vip'
import * as GameApi from '@/api/game'
import { trackClickNavHome, trackClickNavPromotion, trackClickNavDeposit, trackClickNavWithdraw, trackClickNavProfile } from '@/utils/firebase-analytics'
import { getAssetsFile } from "@/utils/pub-use"
// 移除认证弹窗工具函数导入，因为现在是全局的
// import { showLoginDialog } from '@/utils/auth-dialog'

const route = useRoute()
const userStore = useUserStore()
const uiStore = useUIStore()
const { t } = useI18n()

// 移除本地的认证弹窗状态管理
// const showAuthDialog = ref(false)
// const isLoginMode = ref(true)

const messageStore = useMessageStore()
const vipStore = useVipStore()

// 添加推广员信息状态
const promoterInfo = ref<any>({
  commission: 0,
  profit: 0,
  promoterNum: 0
})

// 添加宝箱信息状态
const chestData = ref<any[]>([])

// 检查个人中心是否有红点
const hasPersonBadge = computed(() => {
  // 站内信红点
  const hasMessageBadge = messageStore.count > 0
  // VIP红点
  const hasVipBadge = vipStore.vipData?.drawRewardList && vipStore.vipData.drawRewardList.length > 0
  // 推广员红点
  const hasPromoterBadge = promoterInfo.value?.commission > 0

  return hasMessageBadge || hasVipBadge || hasPromoterBadge
})

// 检查活动是否有红点（需要用户已登录）
const hasActivityBadge = computed(() => {
  // 只有用户已登录时才显示活动红点
  if (!userStore.isLoggedIn) return false

  // UI store中的活动红点
  const hasUIBadge = uiStore.hasAnyActivityBadge
  // 宝箱红点 - 检查是否有可领取的宝箱
  const hasChestBadge = chestData.value.some(chest => chest.status === 1) // status为1表示可领取

  return hasUIBadge || hasChestBadge
})

// 初始化个人中心红点数据
const initPersonBadgeData = async () => {
  if (!userStore.isLoggedIn) return

  try {
    // 并行加载消息数量、VIP数据和推广员信息
    await Promise.all([
      // 获取消息数量
      (async () => {
        try {
          const msgRes = await GameApi.siteMsglist({
            pageNo: 1,
            pageSize: 50,
            msgType: ''
          })
          const unreadCount = msgRes.data.list?.filter(item => !item.read).length || 0
          messageStore.setCount(unreadCount)
        } catch (error) {
          console.error('获取消息数量失败:', error)
        }
      })(),
      // 加载VIP数据
      vipStore.loadVipData(),
      // 获取推广员信息
      (async () => {
        try {
          const promoterRes = await GameApi.userpromoterInfo()
          if (promoterRes && promoterRes.data) {
            promoterInfo.value = promoterRes.data
          }
        } catch (error) {
          console.error('获取推广员信息失败:', error)
        }
      })(),
      // 获取宝箱数据
      (async () => {
        try {
          const chestRes = await GameApi.getPromoterBox({
            page: 1,
            pageSize: 50
          })
          if (chestRes && chestRes.data && chestRes.data.list) {
            chestData.value = chestRes.data.list
          }
        } catch (error) {
          console.error('获取宝箱数据失败:', error)
        }
      })()
    ])
  } catch (error) {
    console.error('初始化个人中心红点数据失败:', error)
  }
}

// 清除红点数据
const clearPersonBadgeData = () => {
  messageStore.setCount(0)
  // 重置VIP数据
  vipStore.$reset()
  // 重置推广员信息
  promoterInfo.value = {
    commission: 0,
    profit: 0,
    promoterNum: 0
  }
  // 重置宝箱数据
  chestData.value = []
}

onMounted(() => {
  // 如果用户已登录，初始化红点数据
  if (userStore.isLoggedIn) {
    initPersonBadgeData()
  }
})

// 监听用户登录状态变化
watch(
  () => userStore.isLoggedIn,
  (isLoggedIn) => {
    if (isLoggedIn) {
      // 用户登录时，初始化红点数据
      initPersonBadgeData()
    } else {
      // 用户退出时，清除红点数据
      clearPersonBadgeData()
    }
  }
)

const tabPaths = ['/', '/activity', '/Deposito', '/wallet', '/person']
const active = computed(() => {
  if (route.path === '/') return 0
  const idx = tabPaths.findIndex((path, i) => i !== 0 && route.path.startsWith(path))
  return idx === -1 ? 0 : idx
})

async function beforeChange(to, from) {
  // 根据点击的哪一个来上报
  switch (to) {
    case 0:
      trackClickNavHome()
      break
    case 1:
      trackClickNavPromotion()
      // 活动页不需要登录，允许直接跳转
      break
    case 2:
      trackClickNavDeposit()
      break
    case 3:
      trackClickNavWithdraw()
      break
    case 4:
      trackClickNavProfile()
      break
    default:
      break
  }
  // 如果用户未登录，除了首页(索引0)和活动页(索引1)外，其他所有tab都需要登录
  if (to !== 0 && to !== 1 && !userStore.isLoggedIn) {
    window.$showLoginDialog() // 使用全局函数
    return false // 阻止跳转
  }
  return true
}

// 移除这些函数，因为现在使用全局状态管理
// function handleClose() {
//   showAuthDialog.value = false
// }
// function openLogin() {
//   isLoginMode.value = true
//   showAuthDialog.value = true
// }
// function handleSwitch(mode) {
//   isLoginMode.value = mode
// }
</script>
<style lang="scss" scoped>
:deep(.van-tabbar-item--active),
:deep(.van-tabbar-item.van-tabbar-item--active) {
  position: relative;
  z-index: 2;

  .van-badge__wrapper {
    position: relative;
    z-index: 3;
  }

  .van-badge__wrapper .van-icon img {
    transform: translate(-50%, -50%);
    animation: scaleIn 0.3s ease-out forwards;
  }

  .van-tabbar-item__text {
    position: relative;
    text-shadow: 1px 1px 3px rgb(36 202 187), 2px 4px 7px rgb(36 202 187 / 35%);

    z-index: 3;
  }

  &::before {
    background: getPath('tabBar/tabbarActiveBg.png') no-repeat;
    background-size: 70px 48px;
    background-position: 0 -2px;
    display: block;
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 70px;
    height: 48px;
    border-radius: 7px;
    z-index: 3;
    transform: translate(-50%, -50%);
    animation: scaleInOut 0.3s ease-out forwards;
  }
}

@keyframes scaleInOut {
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }

  25% {
    transform: translate(-50%, -50%) scale(1.1);
    opacity: 1;
  }

  50% {
    transform: translate(-50%, -50%) scale(1.2);
    opacity: 1;
  }

  80% {
    transform: translate(-50%, -50%) scale(1.1);
    opacity: 1;
  }

  100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
}

@keyframes scaleIn {
  0% {
    transform: translate(0%, -12%) scale(1);
    opacity: 1;
  }

  25% {
    transform: translate(0%, -12%) scale(1.05);
    opacity: 1;
  }

  50% {
    transform: translate(0%, -12%) scale(1.1);
    opacity: 1;
  }

  80% {
    transform: translate(0%, -12%) scale(1.15);
    opacity: 1;
  }

  100% {
    transform: translate(0%, -12%) scale(1.2);
    opacity: 1;
  }
}

.tab-content {
  position: relative;
  display: flex;
  align-items: center;
}

.tab-badge-dot {
  position: absolute;
  top: -28px;
  right: -5px;
  width: 10px;
  height: 10px;
  background: #FF3B30;
  border-radius: 50%;
  // animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }

  50% {
    transform: scale(1.2);
    opacity: 0.8;
  }

  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.tab-bar-container {
  position: fixed;
  z-index: 999;
  bottom: 0;
  left: 0;
  width: 100vw;
  height: 68px;

  background: getPath('tabBar/tabbarBg.png') no-repeat;
  background-size: 100% 100%;
}

.tab-bar {
  padding: 0 30px;
  box-sizing: border-box;
  --van-cell-group-inset-padding: 0;
  --van-tabbar-height: 98px;
  --van-tabbar-background: transparent;
  background-size: 100% 100%;
  --van-tabbar-item-text-color: #0A988A;
  --van-tabbar-item-active-color: #fff;
  --van-tabbar-item-font-size: 11px;
  --van-tabbar-item-active-background: linear-gradient(161deg, #feee45, #dba320);
  --van-tabbar-item-icon-size: 88px;
  --van-tabbar-item-icon-margin-bottom: -3px;
  z-index: 99;

}

:deep(.van-badge__wrapper .van-icon img) {
  width: 44px;
  height: 44px;
}

/* TabBar红点样式 */
.tabbar-badge-dot {
  width: 8px;
  height: 8px;
  background: #FF3B30;
  border-radius: 50%;
  position: absolute;
  top: 2px;
  right: 50%;
  transform: translateX(50%);
}
</style>
